import React from 'react';
import PageHeader from '../components/PageHeader';
import Callout from '../components/Callout';
import { motion } from 'framer-motion';

const Design = () => {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="prose-custom">
      <PageHeader 
        badge="Stage 1"
        title="Experimental Design & Planning" 
        subtitle="The upstream stage that is most often neglected. A perfectly executed analysis cannot save fundamentally flawed design."
      />

      <p>
        Many reproducibility failures originate before a single pipette is lifted or line of code is written. 
        When we do not rigidly separate hypothesis-generating (exploratory) research from hypothesis-testing 
        (confirmatory) research, we invite cognitive biases into our results.
      </p>

      <h2>Pre-registration & Registered Reports</h2>
      <p>
        <strong>Pre-registration</strong> is the practice of publishing your study design, hypotheses, and analysis 
        plan in a time-stamped, read-only repository <em>before</em> data collection begins. This prevents the blending 
        of exploration and confirmation.
      </p>
      <p>
        <strong>Registered Reports</strong> take this a step further. It is a publishing format where peer review 
        happens before the data is collected. If the design is sound, the journal guarantees publication regardless 
        of whether the results are positive, negative, or inconclusive. This effectively eliminates publication bias.
      </p>

      <h2>Avoiding Common Pitfalls</h2>
      <ul className="space-y-4">
        <li>
          <strong>p-hacking:</strong> Running multiple analyses until you find a statistically significant result, 
          then reporting only that analysis. Pre-registration prevents this.
        </li>
        <li>
          <strong>HARKing (Hypothesizing After Results are Known):</strong> Presenting an unexpected finding as if 
          you had predicted it all along. It artificially inflates statistical confidence.
        </li>
        <li>
          <strong>The Garden of Forking Paths:</strong> When researchers have degrees of freedom in how to process 
          and analyze data. Even without malicious intent, researchers often subconsciously choose paths that yield 
          significant results.
        </li>
        <li>
          <strong>Pseudoreplication:</strong> Analyzing sub-samples as if they were independent samples (e.g., measuring 
          10 cells from 1 mouse and claiming n=10 instead of n=1).
        </li>
      </ul>

      <h2>Field-Specific Reporting Standards</h2>
      <Callout type="info" title="Find the standard for your field">
        You do not need to memorize every guideline. Visit the <strong>EQUATOR Network</strong> to find the reporting 
        guideline specific to your study type before you begin designing it.
      </Callout>
      
      <p>Adhering to community standards ensures you capture the metadata necessary for methods reproducibility:</p>
      <ul>
        <li><strong>ARRIVE:</strong> Animal research reporting of in vivo experiments.</li>
        <li><strong>CONSORT:</strong> Randomized controlled clinical trials.</li>
        <li><strong>STROBE:</strong> Observational studies in epidemiology.</li>
        <li><strong>PRISMA:</strong> Systematic reviews and meta-analyses.</li>
        <li><strong>MIAME/MIQE:</strong> Microarray and quantitative PCR experiments.</li>
      </ul>
    </motion.div>
  );
};

export default Design;